import { ClipboardEvent, KeyboardEvent, useRef } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/ui/utils';

interface OtpInputProps {
  value: string;
  length?: number;
  disabled?: boolean;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
}

const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

export const OtpInput = ({
  value,
  length = 6,
  disabled = false,
  onChange,
  onComplete,
}: OtpInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const focusAt = (index: number) => {
    inputsRef.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const commit = (next: string) => {
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  const handleChange = (index: number, raw: string) => {
    const typed = digitsOnly(raw);
    if (!typed) return;

    // Take the last character so retyping over a filled box replaces it.
    const characters = value.padEnd(length, ' ').split('');
    characters[index] = typed[typed.length - 1];

    const next = characters.join('').trimEnd();
    commit(next);
    focusAt(index + 1);
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const characters = value.padEnd(length, ' ').split('');

      // Empty box steps back and clears the previous one.
      const target = characters[index].trim() ? index : index - 1;
      if (target < 0) return;

      characters[target] = ' ';
      onChange(characters.join('').trimEnd());
      focusAt(target);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = digitsOnly(event.clipboardData.getData('text')).slice(
      0,
      length
    );
    if (!pasted) return;

    commit(pasted);
    focusAt(pasted.length);
  };

  return (
    <div className="flex justify-between gap-2">
      {Array.from({ length }).map((_, index) => (
        <Input
          key={index}
          ref={(element) => {
            inputsRef.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          // Lets iOS and Chrome fill the code straight from the email.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1}`}
          maxLength={1}
          disabled={disabled}
          value={value[index]?.trim() || ''}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          className={cn(
            'h-12 flex-1 text-center text-lg font-medium',
            'focus-visible:ring-2 focus-visible:ring-brand-600'
          )}
        />
      ))}
    </div>
  );
};
